// Ein offener Chat (docs/claude.md): Verlauf, Live-Text und die Handgriffe.
//
// Inhalte gibt es nur nach der Vault-PIN. Der Verlauf kommt per GET, alles
// Neue über einen eigenen Socket (/ws/claude/<id>), der wie das Terminal
// die PIN prüft und beim Ablauf mit 4423 schließt. Reihenfolge beim
// Verbinden: erst den Socket öffnen (und puffern), dann den Verlauf holen
// -- so fällt nichts in die Lücke dazwischen. Jedes Ereignis trägt eine
// laufende Nummer; Doppeltes fällt weg.
//
// Der Entwurf im Feld (internal/claude/entwurf.go) kommt über denselben
// Socket: beim Verbinden einmal geholt, danach jede Änderung als Ereignis
// „entwurf". Der neue Chat („neu") hat keinen Verlauf, aber einen Socket
// für seinen Entwurf.
//
// Auch die offenen Rückfragen GANZ (Befehl, Datei, Fragen an dich) kommen
// nur über diesen Socket: gleich beim Verbinden und bei jeder Änderung als
// Ereignis „offen". Der freie Stand nennt nur ihr Werkzeug
// (Sicherheits-Nachprüfung 21.10.2026, Befund 2).

import { APIError, csrfHeader, deleteJSON, getJSON, patchJSON, postJSON, putJSON } from "../api";
import { PIN_NOETIG } from "../pin-stufe";
import { ladeHoch } from "./anhaenge";
import type { Anmeldung, Chat, Diff, Entwurf, EntwurfFelder, Ereignis, Rueckfrage } from "./claude";

export const GESPERRT = 4423;

export async function pinNoetig(): Promise<boolean> {
  try {
    const st = await getJSON<{ configured: boolean; unlocked: boolean }>("/v2-api/vault/pin/status");
    return st.configured && !st.unlocked;
  } catch {
    return false; // keine Auskunft: verbinden, der Server entscheidet
  }
}

/** Ereignisse je Stück des Verlaufs: ein langer Chat kam sonst ganz --
 *  30 MB JSON, 44 MB im Speicher je offenem Chat (Messung 20.10.2026). */
export const VERLAUF_SEITE = 600;
// Länger still als der Puls des Servers (30 s, chatPuls in internal/api/
// claude.go) plus Spiel: die Leitung ist tot (ChatVerbindung.wach).
const STILL_MS = 40_000;

/** Ein Stück des Verlaufs (internal/claude Seite). */
export interface Seite {
  ereignisse: Ereignis[];
  frueher: number;
}

export class ChatVerbindung {
  ereignisse = $state.raw<Ereignis[]>([]);
  /** Text, während Claude ihn schreibt (noch nicht im Verlauf) */
  live = $state("");
  geladen = $state(false);
  gesperrt = $state(false);
  verbunden = $state(false);
  fehler = $state<string | null>(null);
  /** der Entwurf im Feld, wie ihn die Brücke hält */
  entwurf = $state.raw<Entwurf | null>(null);
  /** die offenen Rückfragen ganz, wie sie der Socket zuletzt brachte
   *  (Ereignis „offen"). Welche offen SIND, sagt der Stand
   *  (offeneFreigaben in ./claude). */
  offen = $state.raw<Rueckfrage[]>([]);
  /** So viele gespeicherte Ereignisse liegen noch vor den geladenen
   *  (VERLAUF_SEITE: nur das Ende kommt gleich, Früheres auf Abruf). */
  frueher = $state(0);
  #holtFrueher = false;

  #id: string;
  #ws: WebSocket | null = null;
  #puffer: Ereignis[] | null = null;
  #max = 0;
  #aus = false;
  #warte = 1000;
  #timer: ReturnType<typeof setTimeout> | undefined;
  // Wann zuletzt etwas kam (Ereignis oder Puls, alle 30 s vom Server).
  #zuletzt = 0;
  #wache: ReturnType<typeof setInterval> | undefined;
  #sichtbar = (): void => {
    if (document.visibilityState === "visible") this.wach();
  };
  #zurueck = (): void => this.wach();

  constructor(id: string) {
    this.#id = id;
  }

  async start(): Promise<void> {
    this.#aus = false;
    if (await pinNoetig()) {
      this.gesperrt = true;
      return;
    }
    this.gesperrt = false;
    this.#verbinde();
    // Die Leitung bewachen (21.10.2026: „Der Chat hing fest. Erst ein
    // Neuladen der Seite zeigte wieder was"): kommt 75 s lang nichts, auch
    // kein Puls, gilt sie als tot und wird neu aufgebaut; und zurück im
    // Tab wird eine abgerissene sofort neu verbunden, statt auf den Takt
    // zu warten, den Safari im Hintergrund anhält.
    clearInterval(this.#wache);
    this.#wache = setInterval(() => {
      const ws = this.#ws;
      if (ws && ws.readyState === WebSocket.OPEN && Date.now() - this.#zuletzt > 75_000) ws.close();
    }, 20_000);
    document.removeEventListener("visibilitychange", this.#sichtbar);
    document.addEventListener("visibilitychange", this.#sichtbar);
    // Nach dem Ruhezustand kommt „visible" nicht immer (Lesson
    // Sichtbarkeit nach Schlaf): auch Fokus, Rückkehr aus dem Cache und
    // wieder Netz zählen als Rückkehr.
    for (const art of ["focus", "pageshow", "online"]) {
      window.removeEventListener(art, this.#zurueck);
      window.addEventListener(art, this.#zurueck);
    }
  }

  stop(): void {
    this.#aus = true;
    if (this.#timer) clearTimeout(this.#timer);
    clearInterval(this.#wache);
    document.removeEventListener("visibilitychange", this.#sichtbar);
    for (const art of ["focus", "pageshow", "online"]) window.removeEventListener(art, this.#zurueck);
    this.#ws?.close();
    this.#ws = null;
    this.verbunden = false;
  }

  /** Jemand ist wieder da (Tab vorn, Fenster im Fokus, Klick in den Chat):
   *  eine abgerissene Leitung sofort neu, eine stille auch. Ein Mac, der
   *  eine Weile lag, hält den Socket für offen, obwohl nichts mehr kommt --
   *  die Wache merkte das erst nach bis zu 95 s, und so lange kam kein
   *  Ereignis an (Tim 31.10.2026: „Neu beginnen" an der Konstruktion zeigte
   *  erst nach dem Neuladen etwas). Der Puls kommt alle 30 s; länger still
   *  heißt tot. */
  wach(): void {
    if (this.#aus || this.gesperrt) return;
    const ws = this.#ws;
    if (!ws) return this.#gleichVerbinden();
    if (ws.readyState === WebSocket.OPEN && Date.now() - this.#zuletzt > STILL_MS) {
      clearTimeout(this.#timer);
      this.#warte = 1000;
      this.verbunden = false;
      this.#verbinde();
    }
  }

  /** Abgerissen und nicht gesperrt: jetzt neu verbinden, nicht erst nach
   *  der Wartezeit. */
  #gleichVerbinden(): void {
    if (this.#aus || this.gesperrt || this.#ws) return;
    clearTimeout(this.#timer);
    this.#warte = 1000;
    void (async () => {
      if (await pinNoetig()) {
        this.gesperrt = true;
        return;
      }
      if (!this.#aus && !this.#ws) this.#verbinde();
    })();
  }

  #verbinde(): void {
    // Höchstens eine Leitung: eine ältere wird abgelöst und geschlossen.
    const alt = this.#ws;
    this.#ws = null;
    alt?.close();
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/claude/${encodeURIComponent(this.#id)}`);
    this.#ws = ws;
    this.#puffer = [];
    // Jeder Handler fragt zuerst, ob er noch die Leitung DIESER Verbindung
    // ist. Ein abgelöster Socket (Wache, Rückkehr in den Tab) darf weder
    // Live-Text anhängen noch den Puffer teilen: sonst stand jedes Stück
    // Text doppelt da.
    const meiner = (): boolean => this.#ws === ws;
    ws.onopen = async () => {
      if (!meiner()) {
        ws.close();
        return;
      }
      this.verbunden = true;
      this.#warte = 1000;
      this.#zuletzt = Date.now();
      void this.#holeEntwurf();
      try {
        if (this.#id !== "neu") {
          const v = await getJSON<Seite>(`/v2-api/claude/chats/${encodeURIComponent(this.#id)}/verlauf?anzahl=${VERLAUF_SEITE}`);
          if (!meiner()) return;
          this.#setze(v?.ereignisse ?? [], this.#puffer ?? []);
          this.frueher = v?.frueher ?? 0;
        }
        this.fehler = null;
      } catch (e) {
        if (!meiner()) return;
        this.fehler = e instanceof Error ? e.message : String(e);
      }
      this.#puffer = null;
      this.geladen = true;
    };
    ws.onmessage = (m) => {
      if (!meiner()) return;
      this.#zuletzt = Date.now();
      let e: Ereignis;
      try {
        e = JSON.parse(String(m.data)) as Ereignis;
      } catch {
        return;
      }
      if ((e.art as string) === "puls") return;
      if (e.art === "entwurf") {
        this.#nimmEntwurf(e.daten as unknown as Entwurf);
        return;
      }
      // Ein Zustand, kein Eintrag im Verlauf: ohne Nummer, gilt sofort.
      if (e.art === "offen") {
        const d = e.daten as unknown;
        this.offen = Array.isArray(d) ? (d as Rueckfrage[]) : [];
        return;
      }
      if (this.#puffer) this.#puffer.push(e);
      else this.#nimm(e);
    };
    ws.onclose = (ev) => {
      // Ein abgelöster Socket meldet nichts mehr und plant nichts.
      if (!meiner()) return;
      this.verbunden = false;
      this.#ws = null;
      if (ev.code === GESPERRT) {
        this.gesperrt = true;
        return;
      }
      if (this.#aus) return;
      // Ein 423 vor dem Upgrade sieht der Browser nur als „geschlossen":
      // vor dem nächsten Versuch die PIN fragen.
      clearTimeout(this.#timer);
      this.#timer = setTimeout(async () => {
        if (this.#aus) return;
        if (await pinNoetig()) {
          this.gesperrt = true;
          return;
        }
        // Während der Frage kann #gleichVerbinden (Rückkehr in den Tab)
        // schon neu verbunden haben -- dann nicht noch einen Socket.
        if (this.#aus || this.#ws) return;
        this.#verbinde();
      }, this.#warte);
      this.#warte = Math.min(this.#warte * 2, 15_000);
    };
  }

  // Nach jedem Verbinden frisch: was während der Trennung geschah, kam
  // nicht als Ereignis.
  async #holeEntwurf(): Promise<void> {
    try {
      const d = await getJSON<Entwurf>(entwurfPfad(this.#id));
      if (d && (!this.entwurf || d.version >= this.entwurf.version)) this.entwurf = d;
    } catch {
      /* gesperrt oder Brücke weg: der Socket meldet sich dann selbst */
    }
  }

  #nimmEntwurf(d: Entwurf | undefined): void {
    if (d && (!this.entwurf || d.version >= this.entwurf.version)) this.entwurf = d;
  }

  /** Das nächste Stück vor dem Geladenen („⋯ frühere"). */
  async ladeFrueher(): Promise<void> {
    const erste = this.ereignisse[0]?.seq;
    if (this.#holtFrueher || !this.frueher || !erste) return;
    this.#holtFrueher = true;
    try {
      const v = await getJSON<Seite>(`/v2-api/claude/chats/${encodeURIComponent(this.#id)}/verlauf?anzahl=${VERLAUF_SEITE}&vor=${erste}`);
      if (this.ereignisse[0]?.seq !== erste) return; // inzwischen neu geladen
      this.ereignisse = [...(v?.ereignisse ?? []), ...this.ereignisse];
      this.frueher = v?.frueher ?? 0;
    } finally {
      this.#holtFrueher = false;
    }
  }

  #setze(verlauf: Ereignis[], puffer: Ereignis[]): void {
    let max = 0;
    for (const e of verlauf) max = Math.max(max, e.seq ?? 0);
    const neu = [...verlauf];
    let live = "";
    for (const e of puffer) {
      if (e.art === "teil") {
        live += e.text ?? "";
        continue;
      }
      if ((e.seq ?? 0) > max) {
        neu.push(e);
        max = e.seq ?? max;
        if (beendetText(e)) live = "";
      }
    }
    this.#max = max;
    this.ereignisse = neu;
    this.live = live;
  }

  #nimm(e: Ereignis): void {
    if (e.art === "teil") {
      this.live += e.text ?? "";
      return;
    }
    if ((e.seq ?? 0) <= this.#max) return;
    this.#max = e.seq ?? this.#max;
    this.ereignisse = [...this.ereignisse, e];
    if (beendetText(e)) this.live = "";
  }
}

// Kommt der fertige Text (oder endet die Antwort), ist der Live-Text
// überholt.
function beendetText(e: Ereignis): boolean {
  if (e.art === "zustand") return true;
  if (e.art !== "claude" || !e.roh) return false;
  const t = (e.roh as { type?: string }).type;
  if (t === "result") return true;
  if (t !== "assistant") return false;
  const c = (e.roh as { message?: { content?: { type?: string }[] } }).message?.content ?? [];
  return c.some((b) => b?.type === "text");
}

// ─── Handgriffe (alle hinter der PIN, Schutzstufe) ──────────────────

const pfad = (id: string) => `/v2-api/claude/chats/${encodeURIComponent(id)}`;

type Datei = { name: string; typ: string; daten: string };

export interface NeuerChat {
  repo: string;
  /** woran der Chat arbeitet, z. B. "konstruktion:Fahrzeug/Schlüsselhülle" */
  bezug?: string;
  /** geht bei jedem Start in den System-Prompt (internal/claude, Chat.Kontext) */
  kontext?: string;
  modell?: string;
  effort?: string;
  text: string;
  /** Anhänge, die schon im Entwurf „neu" liegen (Datei) */
  entwurf_anhaenge?: string[];
  /** aus welchem Fenster (der geleerte Entwurf ist sein Echo) */
  fenster?: string;
}

export function neuerChat(n: NeuerChat): Promise<Chat> {
  return postJSON<Chat>("/v2-api/claude/chats", n);
}

/** Aus dem Feld geschickt: Text und die Anhänge des Entwurfs (Datei);
 *  danach ist der Entwurf auf jedem Gerät leer. */
export function sende(id: string, text: string, entwurfAnhaenge: string[] = [], fenster = ""): Promise<void> {
  return postJSON<void>(`${pfad(id)}/nachricht`, { text, entwurf_anhaenge: entwurfAnhaenge, fenster });
}

// ─── Entwurf (internal/claude/entwurf.go) ───────────────────────────

const entwurfPfad = (s: string) => `/v2-api/claude/entwurf/${encodeURIComponent(s)}`;

/** Den Text (beim neuen Chat dazu Repo, Modell, Effort) sichern. */
export function setzeEntwurf(s: string, f: EntwurfFelder, fenster: string): Promise<Entwurf> {
  return putJSON<Entwurf>(entwurfPfad(s), { ...f, fenster });
}

/** Einen Anhang gleich beim Einfügen hochladen, mit Fortschritt (0..1). */
export async function legeEntwurfAnhangAb(s: string, a: Datei, fenster: string, fortschritt?: (anteil: number) => void): Promise<Entwurf> {
  const pfad = `${entwurfPfad(s)}/anhang`;
  const r = await ladeHoch(pfad, { ...a, fenster }, csrfHeader(), fortschritt);
  let j: unknown = null;
  try {
    j = r.text ? JSON.parse(r.text) : null;
  } catch {
    /* kein JSON */
  }
  if (r.status >= 200 && r.status < 300) return j as Entwurf;
  // Die PIN-Abfrage hängt an fetch (lib/pin-stufe.ts) -- hier selbst.
  if (r.status === 423 && (j as { stufe?: unknown } | null)?.stufe === true) window.dispatchEvent(new CustomEvent(PIN_NOETIG));
  const satz = (j as { error?: string } | null)?.error ?? `HTTP ${r.status}`;
  throw new APIError(r.status, `${satz} (${pfad})`, satz);
}

export function entferneEntwurfAnhang(s: string, datei: string, fenster: string): Promise<Entwurf> {
  return deleteJSON<Entwurf>(`${entwurfPfad(s)}/anhang/${encodeURIComponent(datei)}?fenster=${encodeURIComponent(fenster)}`);
}

/** Modell (gilt sofort), Effort (ab der nächsten Nachricht), Titel;
 *  neu_beginnen = neue Claude-Sitzung im selben Chat (Tim 31.10.2026). */
export function aendere(id: string, e: { titel?: string; modell?: string; effort?: string; archiviert?: boolean; neu_beginnen?: boolean }): Promise<void> {
  return patchJSON<void>(pfad(id), e);
}

/** Adresse eines Anhangs (hinter der PIN wie der Verlauf). */
export function anhangUrl(id: string, datei: string): string {
  return `${pfad(id)}/anhang/${encodeURIComponent(datei)}`;
}

/** Die kleine Fassung eines Bild-Anhangs für die Anzeige im Chat (höchstens
 *  640 px, internal/bildvorschau). Volle Bildschirmfotos als Briefmarken
 *  trieben den Safari-Tab über die Speichergrenze (28.10.2026). Der Link
 *  daneben öffnet weiter das Original (anhangUrl). */
export function anhangVorschauUrl(id: string, datei: string): string {
  return `${anhangUrl(id, datei)}?vorschau=1`;
}

export interface AntwortDaten {
  id: string;
  erlauben: boolean;
  immer?: boolean;
  grund?: string;
  antworten?: Record<string, string>;
}

export function antworte(id: string, a: AntwortDaten): Promise<void> {
  return postJSON<void>(`${pfad(id)}/antwort`, a);
}

export function stoppe(id: string): Promise<void> {
  return postJSON<void>(`${pfad(id)}/stopp`, {});
}

export function benenne(id: string, titel: string): Promise<void> {
  return aendere(id, { titel });
}

export function raeumeWeg(id: string): Promise<void> {
  return deleteJSON<void>(pfad(id));
}

// Claude Code beim Konto anmelden (lib/claude/Anmelden.svelte): starten
// liefert den Link zur Anmeldeseite, der Code von dort meldet an.
export async function starteAnmeldung(): Promise<string> {
  return (await postJSON<{ link: string }>("/v2-api/claude/anmeldung", {})).link;
}

export function sendeAnmeldeCode(code: string): Promise<Anmeldung> {
  return postJSON<Anmeldung>("/v2-api/claude/anmeldung/code", { code });
}

export function brecheAnmeldungAb(): Promise<void> {
  return deleteJSON<void>("/v2-api/claude/anmeldung");
}

/** Ein Diff (pages/ClaudeAenderung.svelte): was ein Commit geändert hat
 *  oder was an einer offenen Datei noch nicht committet ist. Code ist
 *  Inhalt -- hinter der PIN wie der Verlauf. */
export class DiffAnsicht {
  diff = $state.raw<Diff | null>(null);
  gesperrt = $state(false);
  laedt = $state(false);
  fehler = $state<string | null>(null);

  async lade(repo: string, ziel: { commit?: string; datei?: string }): Promise<void> {
    if (await pinNoetig()) {
      this.gesperrt = true;
      return;
    }
    this.gesperrt = false;
    this.laedt = true;
    const q = new URLSearchParams();
    if (ziel.commit) q.set("commit", ziel.commit);
    if (ziel.datei) q.set("datei", ziel.datei);
    try {
      this.diff = await getJSON<Diff>(`/v2-api/claude/repos/${encodeURIComponent(repo)}/diff?${q}`);
      this.fehler = null;
    } catch (e) {
      if (e instanceof APIError && e.status === 423) this.gesperrt = true;
      else this.fehler = e instanceof APIError ? e.detail : e instanceof Error ? e.message : String(e);
    } finally {
      this.laedt = false;
    }
  }
}
